import { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useActivateAccount } from '@/shared/hooks';
import {
  Alert,
  AlertDescription,
  Button,
  Input,
  Label,
} from '@/shared/components/ui';
import { Eye, EyeOff, AlertCircle, CheckCircle2 } from 'lucide-react';
import mamLogo from '@/assets/mamLogo.svg';
import { SET_PASSWORD_STRINGS } from '@/shared/constants/strings';
import { SetPasswordFormData, setPasswordSchema } from '@/shared/schemas';

export default function SetPassword() {
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  const activateMutation = useActivateAccount();

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<SetPasswordFormData>({
    resolver: zodResolver(setPasswordSchema),
    mode: 'onSubmit',
    defaultValues: {
      password: '',
      confirmPassword: '',
    },
  });

  const onSubmit = (data: SetPasswordFormData) => {
    if (!token) return;
    activateMutation.mutate({
      token,
      password: data.password,
      confirmPassword: data.confirmPassword,
    });
  };

  // Invalid or missing token
  if (!token) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4">
        <div className="w-full max-w-xl bg-white rounded-2xl shadow-2xl p-5 sm:p-8">
          <div className="flex justify-center mb-8 sm:mb-12">
            <img
              src={mamLogo}
              alt="MamBaby Logo"
              className="w-12 h-12"
              width="48"
              height="48"
              fetchPriority="high"
            />
          </div>
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              {SET_PASSWORD_STRINGS.ERROR_INVALID_TOKEN}
            </AlertDescription>
          </Alert>
          <div className="mt-6 text-center">
            <Link
              to="/login"
              className="font-semibold hover:underline text-brand-blue"
            >
              {SET_PASSWORD_STRINGS.GO_TO_LOGIN}
            </Link>
          </div>
        </div>
      </div>
    );
  }

  // Success state
  if (activateMutation.isSuccess) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4">
        <div className="w-full max-w-xl bg-white rounded-2xl shadow-2xl p-5 sm:p-8">
          <div className="flex justify-center mb-8 sm:mb-12">
            <img
              src={mamLogo}
              alt="MamBaby Logo"
              className="w-12 h-12"
              width="48"
              height="48"
              fetchPriority="high"
            />
          </div>
          <div className="text-center space-y-4">
            <CheckCircle2 className="h-16 w-16 text-green-500 mx-auto" />
            <h1 className="text-2xl font-bold text-foreground">
              {SET_PASSWORD_STRINGS.SUCCESS_TITLE}
            </h1>
            <p className="text-muted-foreground">
              {SET_PASSWORD_STRINGS.SUCCESS_MESSAGE}
            </p>
            <Link
              to="/login"
              className="inline-block mt-4 px-6 py-3 bg-brand-blue text-white rounded-lg font-medium hover:bg-primary-hover"
            >
              {SET_PASSWORD_STRINGS.GO_TO_LOGIN}
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-4">
      <div className="w-full max-w-xl bg-white rounded-2xl shadow-2xl p-5 sm:p-8">
        {/* Header */}
        <div className="flex justify-center mb-8 sm:mb-12">
          <img src={mamLogo} alt="MamBaby Logo" className="w-12 h-12" />
        </div>

        {/* Title */}
        <div className="mb-8">
          <h1 className="text-3xl sm:text-4xl font-bold text-foreground mb-3">
            {SET_PASSWORD_STRINGS.TITLE}
          </h1>
          <p className="text-muted-foreground text-base">
            {SET_PASSWORD_STRINGS.SUBTITLE}
          </p>
        </div>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
          {/* Error Alert */}
          {activateMutation.isError && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                {activateMutation.error?.message ||
                  SET_PASSWORD_STRINGS.ERROR_GENERIC}
              </AlertDescription>
            </Alert>
          )}

          {/* Password Field */}
          <div className="space-y-2">
            <Label
              htmlFor="password"
              className="text-foreground text-sm font-medium"
            >
              {SET_PASSWORD_STRINGS.PASSWORD_LABEL}
            </Label>
            <div className="relative">
              <Input
                id="password"
                type={showPassword ? 'text' : 'password'}
                placeholder={SET_PASSWORD_STRINGS.PASSWORD_PLACEHOLDER}
                {...register('password')}
                className="h-12 bg-input-muted border-0 text-foreground pr-12 focus-visible:ring-2"
                disabled={activateMutation.isPending}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-4 top-1/2 -translate-y-1/2 text-muted-foreground/70 hover:text-muted-foreground"
                aria-label="Toggle password visibility"
              >
                {showPassword ? (
                  <EyeOff className="h-5 w-5" />
                ) : (
                  <Eye className="h-5 w-5" />
                )}
              </button>
            </div>
            {errors.password && (
              <p className="text-sm text-destructive">
                {errors.password.message}
              </p>
            )}
          </div>

          {/* Confirm Password Field */}
          <div className="space-y-2">
            <Label
              htmlFor="confirmPassword"
              className="text-foreground text-sm font-medium"
            >
              {SET_PASSWORD_STRINGS.CONFIRM_PASSWORD_LABEL}
            </Label>
            <div className="relative">
              <Input
                id="confirmPassword"
                type={showConfirmPassword ? 'text' : 'password'}
                placeholder={SET_PASSWORD_STRINGS.CONFIRM_PASSWORD_PLACEHOLDER}
                {...register('confirmPassword')}
                className="h-12 bg-input-muted border-0 text-foreground pr-12 focus-visible:ring-2"
                disabled={activateMutation.isPending}
              />
              <button
                type="button"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                className="absolute right-4 top-1/2 -translate-y-1/2 text-muted-foreground/70 hover:text-muted-foreground"
                aria-label="Toggle confirm password visibility"
              >
                {showConfirmPassword ? (
                  <EyeOff className="h-5 w-5" />
                ) : (
                  <Eye className="h-5 w-5" />
                )}
              </button>
            </div>
            {errors.confirmPassword && (
              <p className="text-sm text-destructive">
                {errors.confirmPassword.message}
              </p>
            )}
          </div>

          {/* Submit Button */}
          <Button
            type="submit"
            className="w-full h-12 text-white text-base font-medium rounded-lg mt-6 bg-brand-blue hover:bg-primary-hover"
            disabled={activateMutation.isPending}
          >
            {activateMutation.isPending ? (
              <span className="flex items-center justify-center">
                <div className="h-5 w-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                {SET_PASSWORD_STRINGS.LOADING_TEXT}
              </span>
            ) : (
              SET_PASSWORD_STRINGS.SUBMIT_BUTTON
            )}
          </Button>

          {/* Back to Login Link */}
          <div className="text-center text-muted-foreground text-sm pt-4">
            <Link
              to="/login"
              className="font-semibold hover:underline text-brand-blue"
            >
              {SET_PASSWORD_STRINGS.GO_TO_LOGIN}
            </Link>
          </div>
        </form>
      </div>
    </div>
  );
}
